<template>
  <div class="commercial-master-budget-view pa-4">
    <!-- 1. Grilla y Filtros gestionados con s-crud -->
    <s-crud
      ref="budgetCrud"
      title="Presupuesto Maestro Comercial"
      :config="config"
      :filter="filter"
      search-input
      singleRow
      add
      @add="openCreateModal"
      edit
      @edit="openEditModal"
      addNoFunction
      @rowSelected="onRowSelected($event)"
      @itemSelected="onItemSelected($event)"
    >
      <!-- Slot de Filtros -->
      <template v-slot:filter>
        <v-container fluid class="pa-4">
          <v-row dense align="center">
            <v-col cols="12" sm="6" md="3">
              <s-select-campaign
                label="Campaña"
                v-model="filter.CmpID"
                clearable
              />
            </v-col>

            <v-col cols="12" sm="6" md="2">
              <s-select-definition
                v-model="filter.TypeDestination"
                :def="1175"
                label="División / Destino"
                clearable
              />
            </v-col>

            <v-col cols="12" sm="6" md="2">
              <s-select-definition
                v-model="filter.EmbStatus"
                :def="1745"
                label="Estado"
                clearable
              />
            </v-col>

            <v-col cols="12" sm="6" md="2">
              <s-date
                label="Fecha Base Desde"
                v-model="filter.DateBegin"
                clearable
              />
            </v-col>

            <v-col cols="12" sm="6" md="2">
              <s-date
                label="Fecha Base Hasta"
                v-model="filter.DateEnd"
                clearable
              />
            </v-col>
          </v-row>
        </v-container>
      </template>

      <!-- Slot de Opciones y Acciones de la Barra -->
      <template v-slot:options>
        <!-- Ver Detalle -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              text
              small
              black
              v-bind="attrs"
              v-on="on"
              @click="openDetailModal(selectedBudget)"
              :disabled="!selectedBudget || !selectedBudget.EmbID"
            >
              <v-icon style="font-size:16px;">mdi-eye-outline</v-icon>
            </v-btn>
          </template>
          <span>Ver Detalle</span>
        </v-tooltip>

        <!-- Aprobar y Bloquear -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              text
              small
              black
              v-bind="attrs"
              v-on="on"
              @click="lockBudget(selectedBudget)"
              :disabled="!selectedBudget || !selectedBudget.EmbID || selectedBudget.EmbStatus !== 1"
            >
              <v-icon style="font-size:16px;">mdi-lock-check</v-icon>
            </v-btn>
          </template>
          <span>Aprobar y Bloquear Presupuesto</span>
        </v-tooltip>

        <!-- Crear Nueva Versión -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              text
              small
              black
              v-bind="attrs"
              v-on="on"
              @click="createNewVersion(selectedBudget)"
              :disabled="!selectedBudget || !selectedBudget.EmbID || selectedBudget.EmbStatus === 3"
            >
              <v-icon style="font-size:16px;">mdi-source-branch-plus</v-icon>
            </v-btn>
          </template>
          <span>Crear Nueva Versión (v{{ (selectedBudget ? selectedBudget.EmbVersion : 1) + 1 }})</span>
        </v-tooltip>

        <!-- Anular -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              text
              small
              black
              v-bind="attrs"
              v-on="on"
              @click="cancelBudget(selectedBudget)"
              :disabled="!selectedBudget || !selectedBudget.EmbID || selectedBudget.EmbStatus === 3"
            >
              <v-icon style="font-size:16px;">mdi-cancel</v-icon>
            </v-btn>
          </template>
          <span>Anular Presupuesto</span>
        </v-tooltip>
      </template>

      <!-- Formateo personalizado de celdas en la grilla -->
      <template v-slot:[`item.EmbName`]="{ item }">
        <span class="font-weight-bold grey--text text--darken-4">{{ item.EmbName }}</span>
      </template>

      <template v-slot:[`item.TypeDestinationName`]="{ item }">
        <v-chip x-small class="font-weight-bold" :color="item.TypeDestination === 1 ? 'green lighten-5' : 'blue lighten-5'" :text-color="item.TypeDestination === 1 ? 'green darken-4' : 'blue darken-4'">
          {{ item.TypeDestinationName }}
        </v-chip>
      </template>

      <template v-slot:[`item.EmbVersion`]="{ item }">
        <v-chip x-small label outlined color="primary" class="font-weight-bold">
          v{{ item.EmbVersion }}
        </v-chip>
      </template>

      <template v-slot:[`item.EmbDate`]="{ item }">
        <span class="caption grey--text text--darken-3">
          {{ item.EmbDate ? $fun.formatDateView(item.EmbDate) : '-' }}
        </span>
      </template>

      <template v-slot:[`item.TotalTons`]="{ item }">
        <span class="font-weight-bold green--text text--darken-3">
          {{ item.TotalTons | formatNumber }} TM
        </span>
      </template>

      <template v-slot:[`item.TotalFcl`]="{ item }">
        <span class="font-weight-bold teal--text text--darken-3">
          {{ item.TotalFcl | formatNumber }} FCL
        </span>
      </template>

      <template v-slot:[`item.TotalFobUsd`]="{ item }">
        <span class="font-weight-bold blue--text text--darken-3">
          ${{ item.TotalFobUsd | formatNumber }}
        </span>
      </template>

      <template v-slot:[`item.StatusName`]="{ item }">
        <v-chip
          x-small
          :color="getStatusColor(item.EmbStatus)"
          text-color="white"
          class="font-weight-bold"
        >
          {{ item.StatusName }}
        </v-chip>
      </template>
    </s-crud>

    <!-- 2. Modales Desacoplados -->
    <commercial-master-budget-form-modal
      v-if="formModal.show"
      v-model="formModal.show"
      :budget-id="formModal.budgetId"
      @saved="onBudgetSaved"
    />

    <commercial-master-budget-detail-modal
      v-if="detailModal.show"
      v-model="detailModal.show"
      :budget-id="detailModal.budgetId"
    />
  </div>
</template>

<script>
import CommercialMasterBudgetFormModal from "./CommercialMasterBudgetFormModal.vue";
import CommercialMasterBudgetDetailModal from "./CommercialMasterBudgetDetailModal.vue";
import _sCommercialMasterBudget from "@/services/ExternalTrade/CommercialMasterBudgetService.js";

export default {
  name: "CommercialMasterBudget",
  components: {
    CommercialMasterBudgetFormModal,
    CommercialMasterBudgetDetailModal,
  },
  filters: {
    formatNumber(val) {
      if (val === null || val === undefined || isNaN(val)) return "0.00";
      return new Intl.NumberFormat("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);
    },
  },
  data() {
    return {
      config: {
        model: {
          EmbID: "ID",
          EmbDate: "date",
        },
        service: _sCommercialMasterBudget,
        headers: [
          { text: "Presupuesto", value: "EmbName" },
          { text: "Versión", value: "EmbVersion" },
          { text: "Destino", value: "TypeDestinationName" },
          { text: "Campaña", value: "CmpName" },
          { text: "Fecha Base", value: "EmbDate" },
          { text: "Moneda", value: "TypeCurrencyName" },
          { text: "Clientes", value: "TotalClients" },
          { text: "Total TM", value: "TotalTons" },
          { text: "Total FCL", value: "TotalFcl" },
          { text: "Total FOB USD", value: "TotalFobUsd" },
          { text: "Estado", value: "StatusName" },
        ],
      },
      filter: {
        TypeDestination: null,
        CmpID: null,
        EmbStatus: null,
        DateBegin: null,
        DateEnd: null,
      },
      selectedBudget: null,
      formModal: {
        show: false,
        budgetId: 0,
      },
      detailModal: {
        show: false,
        budgetId: 0,
      },
    };
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.budgetCrud && !this.$refs.budgetCrud.row) {
        this.$refs.budgetCrud.row = {};
      }
    });
  },
  methods: {
    getStatusColor(status) {
      const map = { 1: "grey darken-1", 2: "success", 3: "error" };
      return map[status] || "grey";
    },
    onRowSelected(val) {
      if (Array.isArray(val)) {
        this.selectedBudget = val.length > 0 ? val[0] : null;
      } else {
        this.selectedBudget = val || null;
      }
    },
    onItemSelected(item) {
      this.selectedBudget = item || null;
    },
    openCreateModal() {
      this.formModal.budgetId = 0;
      this.formModal.show = true;
    },
    openEditModal(item) {
      const budget = item && item.EmbID ? item : this.selectedBudget;
      if (!budget || !budget.EmbID) return;
      this.formModal.budgetId = budget.EmbID;
      this.formModal.show = true;
    },
    openDetailModal(item) {
      const budget = item && item.EmbID ? item : this.selectedBudget;
      if (!budget || !budget.EmbID) return;
      this.detailModal.budgetId = budget.EmbID;
      this.detailModal.show = true;
    },
    onBudgetSaved() {
      if (this.$refs.budgetCrud) {
        this.$refs.budgetCrud.refresh();
      }
    },
    lockBudget(item) {
      if (!item || !item.EmbID) return;

      this.$fun
        .alert(`¿Está seguro de Aprobar y Bloquear el Presupuesto Maestro "${item.EmbName}" (v${item.EmbVersion})? Esta acción fijará la línea base comercial.`, "question")
        .then((r) => {
          if (r && r.value) {
            _sCommercialMasterBudget
              .lock(item.EmbID, this.$fun.getUserID())
              .then(() => {
                this.$fun.alert("Presupuesto Maestro aprobado y bloqueado con éxito.", "success");
                if (this.$refs.budgetCrud) {
                  this.$refs.budgetCrud.refresh();
                }
              })
              .catch((err) => {
                console.error("Error al bloquear presupuesto:", err);
                this.$fun.alert("Error al aprobar y bloquear el presupuesto maestro.", "error");
              });
          }
        });
    },
    createNewVersion(item) {
      if (!item || !item.EmbID) return;

      this.$fun
        .alert(`¿Desea crear una nueva versión basada en "${item.EmbName}" (v${item.EmbVersion})? Se generará una nueva versión editable en borrador conservando las metas actuales.`, "question")
        .then((r) => {
          if (r && r.value) {
            _sCommercialMasterBudget
              .createVersion(item.EmbID, this.$fun.getUserID())
              .then((res) => {
                this.$fun.alert(`Nueva versión creada con éxito (ID: ${res.data}).`, "success");
                if (this.$refs.budgetCrud) {
                  this.$refs.budgetCrud.refresh();
                }
              })
              .catch((err) => {
                console.error("Error al versionar presupuesto:", err);
                this.$fun.alert("Error al generar nueva versión del presupuesto maestro.", "error");
              });
          }
        });
    },
    cancelBudget(item) {
      if (!item || !item.EmbID) return;

      this.$fun
        .alert(`¿Está seguro de anular el Presupuesto Maestro "${item.EmbName}"?`, "question")
        .then((r) => {
          if (r && r.value) {
            _sCommercialMasterBudget
              .cancel(item.EmbID, this.$fun.getUserID())
              .then(() => {
                this.$fun.alert("Presupuesto maestro anulado correctamente.", "success");
                if (this.$refs.budgetCrud) {
                  this.$refs.budgetCrud.refresh();
                }
              })
              .catch((err) => {
                console.error("Error al anular presupuesto:", err);
                this.$fun.alert("Error al anular el presupuesto maestro.", "error");
              });
          }
        });
    },
  },
};
</script>

<style scoped>
</style>
